import { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { DownloadAppDialog } from '@modules/_shared/components/DownloadAppDialog';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SearchInput } from '@modules/_shared/components/search-input/SearchInput';
import { NOTIFICATION_CHANNEL_ICONS } from '@modules/notifications/notificationChannelIcons';
import {
  type NotificationChannelKey,
  type NotificationChannelOption,
  type NotificationSetting,
  REQUIRED_CHANNEL_ERROR,
  type UnusableChannels,
  getNotificationChannelOptions,
  getShownChannelValue,
  getTeamLeaderCcOption,
  isChannelOffered,
  isRequiredChannelLocked,
} from '@modules/notifications/notificationChannels';
import { getNotificationText } from '@modules/notifications/notificationText';
import {
  type NotificationGroup,
  getCollapsedGroupKeys,
  getGroupHeading,
  getGroupKey,
} from '@modules/notifications/preferences/notificationGroups';
import { useSaveNotificationPreferences } from '@modules/notifications/useSaveNotificationPreferences';
import { useUserNotificationSettings } from '@modules/notifications/useUserNotificationSettings';
import { useLocale } from '@shared/hooks/locale';

// The name column takes the free space; channel columns have a fixed width so
// the checkboxes line up under their headers.
const CHANNEL_COLUMN_WIDTH = 96;

const getGridColumns = (channelCount: number) => ({
  xs:
    channelCount === 0
      ? 'minmax(0, 1fr)'
      : `repeat(${channelCount}, minmax(0, 1fr))`,
  sm: [
    'minmax(200px, 1fr)',
    ...Array.from({ length: channelCount }, () => `${CHANNEL_COLUMN_WIDTH}px`),
  ].join(' '),
});

const nameCellSx = { minWidth: 0, gridColumn: { xs: '1 / -1', sm: 'auto' } };

const rowSx = (channelCount: number) => ({
  display: 'grid',
  gridTemplateColumns: getGridColumns(channelCount),
  alignItems: 'center',
  columnGap: 1,
  rowGap: 0.5,
  px: 1.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
});

// The channel heading opens a menu to turn the channel on or off for every
// notification currently shown (after search and collapsing).
const ChannelColumnHeader = ({
  option,
  targets,
  unusable,
  onSetAll,
}: {
  option: NotificationChannelOption;
  targets: readonly NotificationSetting[];
  unusable: UnusableChannels;
  onSetAll: (value: boolean) => void;
}) => {
  const { t } = useLocale();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const Icon = NOTIFICATION_CHANNEL_ICONS[option.channel.name];
  const onCount = targets.filter(setting =>
    getShownChannelValue(setting, option.channel),
  ).length;
  const choose = (value: boolean) => {
    setAnchor(null);
    onSetAll(value);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
      <ButtonBase
        aria-haspopup="menu"
        aria-label={option.label}
        disabled={targets.length === 0}
        onClick={event => setAnchor(event.currentTarget)}
        sx={{
          gap: 0.5,
          px: 0.75,
          py: 0.5,
          borderRadius: 1,
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Icon size={14} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            lineHeight: 1.2,
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {option.label}
        </Typography>
        <ChevronDown size={14} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={anchor != null}
        onClose={() => setAnchor(null)}
      >
        <MenuItem
          disabled={onCount === targets.length}
          onClick={() => choose(true)}
        >
          {t('Turn on for all shown')}
        </MenuItem>
        <MenuItem
          disabled={
            !targets.some(
              setting =>
                getShownChannelValue(setting, option.channel) &&
                !isRequiredChannelLocked(setting, option.channel, unusable),
            )
          }
          onClick={() => choose(false)}
        >
          {t('Turn off for all shown')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

const NotificationPreferencesSkeleton = () => (
  <Stack sx={{ gap: 1 }}>
    {Array.from({ length: 5 }).map((_, index) => (
      <Skeleton key={index} variant="rounded" height={56} />
    ))}
  </Stack>
);

type NotificationPreferencesPanelProps = {
  userId: string;
  // Opens with only this group expanded (a key from getGroupKey).
  focusGroup?: string | null;
};

export const NotificationPreferencesPanel = ({
  userId,
  focusGroup = null,
}: NotificationPreferencesPanelProps) => {
  const { t, language } = useLocale();
  const { query, groups, settings, hasPhoneNumber, hasProApp } =
    useUserNotificationSettings(userId);
  const [savePreferences, saveMutation] =
    useSaveNotificationPreferences(userId);
  const unusableChannels = new Set<NotificationChannelKey>();
  if (!hasPhoneNumber) {
    unusableChannels.add('sms');
  }
  if (!hasProApp) {
    unusableChannels.add('push');
  }
  const [search, setSearch] = useState('');
  const [downloadAppOpen, setDownloadAppOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set(getCollapsedGroupKeys(groups, focusGroup)),
  );

  const channelOptions: NotificationChannelOption[] =
    getNotificationChannelOptions(t).filter(option =>
      settings.some(setting => isChannelOffered(setting, option.channel)),
    );
  const teamLeaderCcOption = getTeamLeaderCcOption(t);

  const getUnavailableReason = (
    option: NotificationChannelOption,
  ): string | null => {
    if (option.channel.name === 'sms' && !hasPhoneNumber) {
      return t(
        'There is no phone number on this profile, so SMS notifications cannot be delivered.',
      );
    }
    if (option.channel.name === 'push' && !hasProApp) {
      // No token means the app is not installed or its notifications are off.
      return t(
        'Push notifications need the PRO Agent app, with notifications allowed on the phone.',
      );
    }
    return null;
  };

  const searchText = search.trim().toLowerCase();
  const matchesSearch = (setting: NotificationSetting) =>
    searchText === '' ||
    [
      getNotificationText(setting, 'label', language),
      getNotificationText(setting, 'description', language),
      setting.label_en,
    ]
      .join(' ')
      .toLowerCase()
      .includes(searchText);
  const visibleGroups = groups
    .map(group => ({
      ...group,
      settings: group.settings.filter(matchesSearch),
    }))
    .filter(group => group.settings.length > 0);
  // While searching, every match is shown, even in a collapsed group.
  const isExpanded = (group: NotificationGroup) =>
    searchText !== '' || !collapsed.has(getGroupKey(group));
  const visibleSettings = visibleGroups.flatMap(group =>
    isExpanded(group) ? group.settings : [],
  );
  const showGroupHeadings = groups.length > 1;
  const allCollapsed =
    groups.length > 0 &&
    groups.every(group => collapsed.has(getGroupKey(group)));

  const save = (
    targets: readonly NotificationSetting[],
    option: NotificationChannelOption,
    value: boolean,
  ) => {
    void savePreferences(
      targets,
      option.channel,
      value,
      unusableChannels,
    ).catch((error: unknown) => {
      if (
        error instanceof Error &&
        error.message.includes(REQUIRED_CHANNEL_ERROR)
      ) {
        void query.refetch();
      }
    });
  };

  const toggleGroup = (group: NotificationGroup) =>
    setCollapsed(previous => {
      const next = new Set(previous);
      const key = getGroupKey(group);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const renderRow = (setting: NotificationSetting) => {
    const name = getNotificationText(setting, 'label', language);
    const description = getNotificationText(setting, 'description', language);

    return (
      <Box key={setting.type} sx={{ ...rowSx(channelOptions.length), py: 1 }}>
        <Box sx={nameCellSx}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {name}
          </Typography>
          {description !== '' && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              {description}
            </Typography>
          )}
          {isChannelOffered(setting, teamLeaderCcOption.channel) && (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={getShownChannelValue(
                    setting,
                    teamLeaderCcOption.channel,
                  )}
                  onChange={event =>
                    save([setting], teamLeaderCcOption, event.target.checked)
                  }
                  slotProps={{
                    input: {
                      'aria-label': `${name} - ${teamLeaderCcOption.label}`,
                    },
                  }}
                />
              }
              label={
                <Typography variant="caption">
                  {t('Send a copy to the team leader')}
                </Typography>
              }
              sx={{ mt: 0.5, ml: 0, gap: 0.5 }}
            />
          )}
        </Box>
        {channelOptions.map(option =>
          isChannelOffered(setting, option.channel) ? (
            <Box
              key={option.channel.name}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <Tooltip
                title={
                  isRequiredChannelLocked(
                    setting,
                    option.channel,
                    unusableChannels,
                  )
                    ? t('At least one notification channel must remain active.')
                    : getUnavailableReason(option) ?? ''
                }
              >
                <span>
                  <Checkbox
                    size="small"
                    disabled={isRequiredChannelLocked(
                      setting,
                      option.channel,
                      unusableChannels,
                    )}
                    checked={getShownChannelValue(setting, option.channel)}
                    onChange={event =>
                      save([setting], option, event.target.checked)
                    }
                    slotProps={{
                      input: {
                        'aria-label': `${name} - ${option.label}`,
                        ...(isRequiredChannelLocked(
                          setting,
                          option.channel,
                          unusableChannels,
                        ) && {
                          'aria-description': t(
                            'At least one notification channel must remain active.',
                          ),
                        }),
                      },
                    }}
                  />
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Typography
              key={option.channel.name}
              variant="body2"
              sx={{ color: 'text.disabled', textAlign: 'center' }}
            >
              —
            </Typography>
          ),
        )}
      </Box>
    );
  };

  const renderGroup = (group: NotificationGroup) => {
    const expanded = isExpanded(group);
    return (
      <Box key={getGroupKey(group)}>
        {showGroupHeadings && (
          <ButtonBase
            onClick={() => toggleGroup(group)}
            disabled={searchText !== ''}
            aria-expanded={expanded}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              gap: 1,
              px: 1.5,
              py: 1,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {getGroupHeading(group.module, t)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {group.settings.length}
            </Typography>
          </ButtonBase>
        )}
        {expanded && group.settings.map(renderRow)}
      </Box>
    );
  };

  const renderContent = () => {
    if (query.loading && query.data == null) {
      return <NotificationPreferencesSkeleton />;
    }

    if (query.error != null) {
      return (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void query.refetch()}
            >
              {t('Retry')}
            </Button>
          }
        >
          {t('Notification settings could not be loaded.')}
        </Alert>
      );
    }

    if (settings.length === 0) {
      return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('There are no notifications to configure right now.')}
          </Typography>
        </Paper>
      );
    }

    const unavailableReasons = channelOptions.flatMap(option => {
      const reason = getUnavailableReason(option);
      return reason == null ? [] : [{ channel: option.channel.name, reason }];
    });

    return (
      <Stack sx={{ gap: 2 }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
        >
          <SearchInput
            value={search}
            placeholder={t('Search notifications')}
            sx={{ flexGrow: 1, minWidth: 200, maxWidth: 360 }}
            onValueChanged={setSearch}
          />
          {showGroupHeadings && (
            <Button
              size="small"
              disabled={searchText !== ''}
              sx={{ ml: 'auto' }}
              onClick={() =>
                setCollapsed(
                  allCollapsed ? new Set() : new Set(groups.map(getGroupKey)),
                )
              }
            >
              {allCollapsed ? t('Expand all') : t('Collapse all')}
            </Button>
          )}
        </Stack>
        {unavailableReasons.map(({ channel, reason }) => (
          <Alert
            key={channel}
            severity="info"
            action={
              channel === 'push' ? (
                <Button
                  color="inherit"
                  size="small"
                  sx={{ whiteSpace: 'nowrap' }}
                  onClick={() => setDownloadAppOpen(true)}
                >
                  {t('Download the app')}
                </Button>
              ) : undefined
            }
          >
            {reason}
          </Alert>
        ))}
        <Paper
          variant="outlined"
          sx={{
            // Clip rows and group headings to the rounded corners. Unlike
            // overflow: hidden, clip keeps the sticky channel header working.
            overflow: 'clip',
            // The last row (or the heading of a collapsed last group) sits on
            // the paper's own border, so it drops its divider.
            '& > :last-child > :last-child': {
              borderBottom: 'none',
            },
          }}
        >
          <Box
            sx={{
              ...rowSx(channelOptions.length),
              py: 0.5,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {t('Notification')}
            </Typography>
            {channelOptions.map(option => {
              const targets = visibleSettings.filter(setting =>
                isChannelOffered(setting, option.channel),
              );
              return (
                <ChannelColumnHeader
                  key={option.channel.name}
                  option={option}
                  targets={targets}
                  unusable={unusableChannels}
                  onSetAll={value =>
                    save(
                      targets.filter(
                        setting =>
                          getShownChannelValue(setting, option.channel) !==
                            value &&
                          (value ||
                            !isRequiredChannelLocked(
                              setting,
                              option.channel,
                              unusableChannels,
                            )),
                      ),
                      option,
                      value,
                    )
                  }
                />
              );
            })}
          </Box>
          {visibleGroups.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}
            >
              {t('No notifications match your search.')}
            </Typography>
          ) : (
            visibleGroups.map(renderGroup)
          )}
        </Paper>
      </Stack>
    );
  };

  return (
    <>
      {renderContent()}
      <MutationErrorSnackbar
        error={
          saveMutation.error?.message.includes(REQUIRED_CHANNEL_ERROR)
            ? new Error(
                t('At least one notification channel must remain active.'),
              )
            : saveMutation.error ?? null
        }
        onClose={() => saveMutation.reset()}
      />
      <DownloadAppDialog
        open={downloadAppOpen}
        onClose={() => setDownloadAppOpen(false)}
        campaign="crm_notification_settings"
      />
    </>
  );
};
